import {
    DefaultButton,
    Dialog, DialogFooter,
    DialogType, Dropdown, IDropdownOption, Stack, TextField
} from "@fluentui/react";
import React, {useEffect, useState} from "react";
import ToggleField from '../Common/ToggleField';
import NumberField from '../Inputs/NumberField';
import {Css} from "nano-css/addon/vcssom/cssToTree";

const typeOptions: IDropdownOption[] = [
    { key: 'string', text: 'String' },
    { key: 'number', text: 'Number' },
    { key: 'boolean', text: 'Boolean' }
];

interface IStorefrontSectionAdvancedStyleEditorModalProps {
    css?: Css;
    displayModal: boolean;
    closer: () => void;
    onChange?: (css: Css) => void;
}

const StorefrontSectionAdvancedStyleEditorModal: React.FC<IStorefrontSectionAdvancedStyleEditorModalProps> = props => {
    const [newKey, setNewKey] = useState('');
    const [newType, setNewType] = useState<'string' | 'number' | 'boolean'>('string');
    const [newValue, setNewValue] = useState<string | number | boolean>('');
    const [workingCss, setWorkingCss] = useState<Css>();

    useEffect(() => {
        setWorkingCss(props.css)
    }, [props.css]);
    
    const updateValue = (key: string, value: any)=> {
        setWorkingCss({...workingCss, [key]: value});
    }
    
    // Helper to render correct input for value type
    const renderValueField = (key: string, value: any) => {
        const valueType = typeof value;
        if (valueType === 'boolean') {
            return (
                <ToggleField
                    label={key}
                    checked={!!value}
                    onChange={(e, v) => updateValue(key, !!v)}
                />
            );
        } else if (valueType === 'number') {
            return (
                <NumberField
                    label={key}
                    value={value}
                    onChange={val => updateValue(key, val)}
                />
            );
        } else {
            return (
                <TextField
                    label={key}
                    value={value ?? ''}
                    onChange={(_, v) => updateValue(key, v)}
                />
            );
        }
    };

    // Helper to parse new value to correct type
    const parseNewValue = () => {
        if (newType === 'boolean') {
            if (typeof newValue === 'boolean') return newValue;
            if (typeof newValue === 'string') return newValue === 'true';
            return false;
        } else if (newType === 'number') {
            return Number(newValue) || 0;
        } else {
            return String(newValue);
        }
    };

    const handleAddKeyValue = () => {
        if (!newKey) return;
        setWorkingCss({...workingCss, [newKey]: parseNewValue()})
        setNewKey('');
        setNewValue('');
        setNewType('string');
    };

    return (
        <Dialog
            hidden={!props.displayModal}
            onDismiss={props.closer}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: 'Modify Style',
            }}
            modalProps={{
                isBlocking: true,
            }}
        >
            {workingCss &&
                <Stack tokens={{ childrenGap: 16 }}>
                    {/* Existing key-value pairs */}
                    {Object.entries(workingCss).map(([key, value]) => (
                        <Stack.Item key={key}>
                            {renderValueField(key, value)}
                        </Stack.Item>
                    ))}
                    {/* Add new key-value pair */}
                    <div style={{backgroundColor: 'lightgray', padding: '1em'}}>
                        <h3>Add New Value</h3>
                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                            <TextField
                                label="Key"
                                value={newKey}
                                onChange={(_, v) => setNewKey(v || '')}
                                styles={{ root: { minWidth: 120 } }}
                            />
                            {newType === 'boolean' ? (
                                <ToggleField
                                    label="Value"
                                    checked={!!newValue}
                                    onChange={(_, v) => setNewValue(!!v)}
                                />
                            ) : newType === 'number' ? (
                                <NumberField
                                    label="Value"
                                    value={typeof newValue === 'number' ? newValue : Number(newValue) || 0}
                                    onChange={val => {
                                        if(val){setNewValue(val)}
                                    }}
                                />
                            ) : (
                                <TextField
                                    label="Value"
                                    value={typeof newValue === 'string' ? newValue : String(newValue)}
                                    onChange={(_, v) => setNewValue(v || '')}
                                />
                            )}
                        </Stack>
                        <Stack>
                            <Dropdown
                                label="Type"
                                options={typeOptions}
                                selectedKey={newType}
                                onChange={(_, v) => setNewType(v?.key as 'string' | 'number' | 'boolean')}
                                styles={{ root: { minWidth: 100 } }}
                            />
    
                            <DefaultButton text="Add" onClick={handleAddKeyValue} disabled={!newKey} style={{marginTop: '10px'}} />
                        </Stack>
                    </div>
                </Stack>
            }
            <DialogFooter>
                <DefaultButton
                    primary={true}
                    text='Apply'
                    onClick={() => {
                        if(workingCss) {
                            props?.onChange?.(workingCss)
                        }
                        props.closer()}}
                />
                <DefaultButton
                    text='Cancel'
                    onClick={() => {
                        props.closer()}}
                />
            </DialogFooter>
        </Dialog>
    );
}
export default StorefrontSectionAdvancedStyleEditorModal;